import React, { useState, useEffect } from 'react';
import { Info, CalendarRange, ChevronLeft, ChevronRight, Activity, MapPin, Clock, Hourglass } from 'lucide-react';
import { useGesture } from '@use-gesture/react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Tour } from '../types/tours';
import { formatTime } from '../utils/dateUtils';
import * as Icons from 'lucide-react';

interface TourCardProps {
  tour: Tour;
  index: number;
  currentImage: number;
  activeTab: Tab;
  onImageChange: (index: number, direction: 'prev' | 'next') => void;
  onTabChange: (index: number, tab: Tab) => void;
  onImageSelect: (index: number, imageIndex: number) => void;
}

const TourCard: React.FC<TourCardProps> = ({
  tour,
  index,
  currentImage,
  activeTab,
  onImageChange,
  onTabChange,
  onImageSelect
}) => {
  const bind = useGesture({
    onDrag: ({ direction: [dx], dragging }) => {
      if (!dragging) {
        if (dx > 0) {
          onImageChange(index, 'prev');
        } else if (dx < 0) {
          onImageChange(index, 'next');
        }
      }
    }
  });

  const carouselImages = tour.tour_images
    ?.filter(img => img.image_type === 'carousel')
    .sort((a, b) => a.display_order - b.display_order)
    .map(img => img.image_url) || [];

  const prices = [
    tour.group_tour_price,
    tour.reduced_group_price,
    tour.private_price,
  ].filter((price): price is number => price !== null && price !== undefined && price > 0);

  const lowestPrice = prices.length > 0 ? Math.min(...prices) : null;

  return (
    <div 
      className="group relative overflow-hidden rounded-[1rem] flex flex-col h-full"
      {...bind()}
    >
      {/* Content Area */}
      <div className="relative aspect-[4/3] overflow-hidden">
        {lowestPrice !== null && (
        <div className="absolute top-4 right-4 z-20 bg-[#953B0E] px-3 py-1 rounded-full text-sm font-semibold border-2 border-[#E85C0D]">
          From ${lowestPrice}
        </div>
        )}

        {carouselImages.length > 0 ? (
          <>
            <div className="absolute inset-0">
              <img
                src={carouselImages[currentImage] || carouselImages[0]}
                alt=""
                className="w-full h-full object-cover blur-md scale-110"
              />
            </div>
            <div className="absolute inset-0">
              {carouselImages.map((image, imgIndex) => (
                <img
                  key={imgIndex}
                  src={image}
                  alt={`${tour.name} - Image ${imgIndex + 1}`}
                  className={`absolute inset-0 w-full h-full object-contain ${currentImage === imgIndex ? 'opacity-100' : 'opacity-0'}`}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="absolute inset-0 bg-[#1F2B1F] flex items-center justify-center">
            <span className="text-white/30 text-sm">No images yet</span>
          </div>
        )}
        
        {/* Image Navigation */}
        {activeTab === 'info' && (
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-4 z-30">
          <button 
            onClick={(e) => {
              e.preventDefault();
              onImageChange(index, 'prev');
            }}
            aria-label="Previous image"
            className="w-12 h-12 bg-[#2A382A]/70 rounded-full flex items-center justify-center hover:bg-[#2A382A] transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button 
            onClick={(e) => {
              e.preventDefault();
              onImageChange(index, 'next');
            }}
            aria-label="Next image" 
            className="w-12 h-12 bg-[#2A382A]/70 rounded-full flex items-center justify-center hover:bg-[#2A382A] transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        )}
        
        {/* Image Indicators */}
        {activeTab === 'info' && (
        <div className="absolute bottom-4 inset-x-0 flex justify-center gap-2 z-30">
          {carouselImages.map((_, imgIndex) => (
            <button
              key={imgIndex}
              onClick={(e) => {
                e.preventDefault();
                onImageSelect(index, imgIndex);
              }}
              aria-label={`Image ${imgIndex + 1} of ${carouselImages.length}`}
              className={`w-4 h-4 rounded-full transition-all border border-solid ${
                currentImage === imgIndex
                  ? 'bg-white w-6 border-white'
                  : 'bg-white/50 hover:bg-white/70 border-white'
              }`}
            />
          ))}
        </div>
        )}
        
        {/* Overlay Content */}
        {activeTab === 'includes' && (
          <div className="absolute inset-0 bg-black/80 p-6 text-white">
            <h4 className="text-xl font-semibold mb-4">What You Get</h4>
            {tour.tour_shared_items?.filter(item => item.shared_items.type === 'include').length ? (
              <ul className="list-disc list-inside space-y-2">
                {tour.tour_shared_items
                  .filter(item => item.shared_items.type === 'include')
                  .sort((a, b) => a.display_order - b.display_order)
                  .map(item => (
                    <li key={item.id} className="flex items-center gap-2">
                      {Icons[item.shared_items.icon as keyof typeof Icons] && 
                        React.createElement(
                          Icons[item.shared_items.icon as keyof typeof Icons],
                          { className: 'w-4 h-4 flex-shrink-0' }
                        )
                      }
                      <h4 className="text-base font-medium">{item.shared_items.title}</h4>
                    </li>
                ))}
              </ul>
            ) : (
              <p>No items listed</p>
            )}
          </div>
        )}
        
        {activeTab === 'bring' && (
          <div className="absolute inset-0 bg-black/80 p-6 text-white">
            <h4 className="text-xl font-semibold mb-4">What to Bring</h4>
            {tour.tour_shared_items?.filter(item => item.shared_items.type === 'bring').length ? (
              <ul className="list-disc list-inside space-y-2">
                {tour.tour_shared_items
                  .filter(item => item.shared_items.type === 'bring')
                  .sort((a, b) => a.display_order - b.display_order)
                  .map(item => (
                    <li key={item.id} className="flex items-center gap-2">
                      {Icons[item.shared_items.icon as keyof typeof Icons] && 
                        React.createElement(
                          Icons[item.shared_items.icon as keyof typeof Icons],
                          { className: 'w-4 h-4 flex-shrink-0' }
                        )
                      }
                      <h4 className="text-base font-medium">{item.shared_items.title}</h4>
                    </li>
                ))}
              </ul>
            ) : (
              <p>No items listed</p>
            )}
          </div>
        )}
      </div>
      
      {/* Tab Buttons */}
      <div className="flex border-b border-white/10">
        {(['info', 'includes', 'bring'] as Tab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => onTabChange(index, tab)}
            className={`flex-1 px-4 py-2 text-xs transition-colors ${
              activeTab === tab 
                ? 'bg-black/90 text-white font-bold border-b-2 border-white' 
                : 'bg-black/70 text-white font-normal hover:bg-black/80'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      
      {/* Footer */}
      <div className="p-6 bg-[#1F2B1F] flex-1 flex flex-col justify-between">
        <h2 className="text-lg font-semibold mb-2">{tour.name}</h2>
        <div className="flex items-center gap-4 text-sm text-white/70 mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>{formatTime(tour.departure).americanTime}</span>
          </div>
          <div className="flex items-center gap-2">
            <Hourglass className="w-4 h-4" />
            <span>{tour.duration}</span>
          </div>
        </div>
        <p className="text-sm text-white/70 mb-8">{tour.tour_card_description}</p>
        <div className="flex gap-2 justify-end">
          <Link
            to={`/tours/${tour.slug}`}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
          >
            <Info className="w-4 h-4" />
            Details
          </Link>
          <Link
            to={`/tours/${tour.slug}/book`}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors border-2 border-[#E85C0D]"
          >
            <CalendarRange className="w-4 h-4" />
            Book Now
          </Link>
        </div>
      </div>
    </div>
  );
};

type Tab = 'info' | 'includes' | 'bring';

const ProjectGrid = () => {
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTabs, setActiveTabs] = useState<{ [key: number]: Tab }>({});
  const [currentImages, setCurrentImages] = useState<{ [key: number]: number }>({});
  const [visibleTours, setVisibleTours] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchTours();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-index'));
            if (!isNaN(index)) {
              setVisibleTours((prev) => new Set([...prev, index]));
            }
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '50px'
      }
    );

    // Observe all tour cards
    document.querySelectorAll('[data-index]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [tours]);

  const fetchTours = async () => {
    try {
      let query = supabase
        .from('tours')
        .select(`
          id,
          name,
          departure,
          duration,
          group_tour_price,
          reduced_group_price,
          private_price,
          activity_level,
          availability,
          location,
          tour_detail_headline,
          tour_detail_description,
          tour_card_description,
          created_at,
          updated_at,
          slug,
          tour_images (
            id,
            image_url,
            image_type,
            display_order
          ),
          tour_shared_items (
            id,
            item_id,
            display_order,
            shared_items (*)
          )
        `);

      query = query.eq('status', 'published')
                  .order('display_order', { ascending: true })
                  .order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      setTours(data || []);
      
      // Initialize states for each tour
      const initialTabs = Object.fromEntries(data?.map((_, i) => [i, 'info' as Tab]) || []);
      const initialImages = Object.fromEntries(data?.map((_, i) => [i, 0]) || []);      
      
      setActiveTabs(initialTabs);
      setCurrentImages(initialImages);
    } catch (err) {
      console.error('Error fetching tours:', err);
      setError('Failed to load tours');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (index: number, direction: 'prev' | 'next') => {
    setCurrentImages(prev => {
      const currentImage = prev[index];
      const totalImages = tours[index].tour_images?.filter(img => img.image_type === 'carousel').length || 0;
      const newImage = direction === 'next' 
        ? (currentImage + 1) % totalImages
        : (currentImage - 1 + totalImages) % totalImages;
      return { ...prev, [index]: newImage };
    });
  };

  const handleTabChange = (index: number, tab: Tab) => {
    setActiveTabs(prev => ({
      ...prev,
      [index]: tab
    }));
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (error) return <div className="text-center py-8 text-red-500">{error}</div>;

  return (
    <div className="px-4 md:px-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr">
        {tours.map((tour, index) => (
          <div
            key={index}
            data-index={index}
            className={`transform transition-all duration-700 h-full ${
              visibleTours.has(index)
                ? 'translate-y-0 opacity-100'
                : 'translate-y-16 opacity-0'
            }`}
          >
            <TourCard
              tour={tour}
              index={index}
              currentImage={currentImages[index]}
              activeTab={activeTabs[index]}
              onImageChange={handleImageChange}
              onTabChange={handleTabChange}
              onImageSelect={(index, imgIndex) => setCurrentImages(prev => ({ ...prev, [index]: imgIndex }))}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectGrid;